import {
  Box,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import ChevronIcon from '../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import DoneIcon from '../../../../packages/common/shared-ui/src/icons/DoneIcon';
import SpreadsheetMobileCreateRow from '../SpreadsheetMobileCreateRow';

// Original class: mobileCreateRoot
const StyledMobileCreateRoot = styled(Grid)(theme => ({
  position: 'relative',
  backgroundColor: '#F1F2F8',
  width: '100%',
  height: '100%',
  zIndex: 1000,
}));
// Original class: saveRow
const StyledSaveRow = styled(IconButton)(theme => ({
  '&.MuiIconButton-root': {
    backgroundColor: '#6270D0',
  },
}));
// Original class: projectSelectMobile
const StyledProjectSelectMobile = styled(FormControl)(({ theme }) => ({
  '&.MuiFormControl-root': {
    marginTop: theme.spacing(9),
  },

  '& .MuiInputLabel-root': {
    top: -12,
    left: -12,
    color: '#111322',
    '&[data-shrink="false"]': {
      top: -4,
      left: 1,
      color: '#7174AC',
    },
    '&.Mui-focused': {
      color: '#111322',
      left: -12,
      top: -12,
    },
  },

  '& .MuiInputBase-root': {
    height: 48,
    backgroundColor: '#ffffff',
    padding: theme.spacing(3, 2, 3, 4),
    borderRadius: theme.spacing(4),
    marginTop: 0,
  },

  '& .MuiSelect-select:focus': {
    backgroundColor: 'transparent',
  },

  '& .MuiTypography-root': {
    lineHeight: 'unset',
  },
}));

const SpreadsheetMobileCreate = ({
  anotherProjects,
  formattedAnotherProjects,
  pageTitle,
  query,
  setQuery,
  formatWeek,
  getAnotherProjectsData,
  intl,
  setEditedFormattedIssues,
  handleSaveSpreadsheet,
  timeType,
  timeTypes,
}) => {
  const [newIssue, setNewIssue] = useState({ issueUniqueId: 0 });
  const [currentWeek, setCurrentWeek] = useState(
    formatWeek({ startOfWeek: query.start, intl }),
  );
  const [weekSum, setWeekSum] = useState(0);

  useEffect(() => {
    if (anotherProjects.length === 0) {
      getAnotherProjectsData({ start: query.start, end: query.end });
    }
  }, [anotherProjects.length, getAnotherProjectsData, query, setQuery]);

  const handleChangeProject = useCallback(
    e => {
      setNewIssue({ projectId: e.target.value, issueUniqueId: 0 });
      setCurrentWeek(formatWeek({ startOfWeek: query.start, intl }));
    },
    [formatWeek, intl, query.start],
  );

  const handleChangeProjectTask = useCallback(
    e => {
      return setNewIssue(prevState => ({
        ...prevState,
        ...formattedAnotherProjects[e.target.value],
      }));
    },
    [formattedAnotherProjects],
  );

  const handleSaveMobileRow = useCallback(async () => {
    const saveResult = await handleSaveSpreadsheet();
    setQuery({ ...query, create: null });
  }, [handleSaveSpreadsheet, query, setQuery]);

  return (
    <StyledMobileCreateRoot container direction='column'>
      <Grid
        container
        wrap='nowrap'
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant='h1'>{pageTitle}</Typography>
        <StyledSaveRow disableRipple onClick={handleSaveMobileRow}>
          <DoneIcon width={24} height={24} viewBox='0 0 24 24' />
        </StyledSaveRow>
      </Grid>
      <Grid
        container
        direction='column'
        sx={{
          marginTop: 2.5,
        }}
      >
        <StyledProjectSelectMobile fullWidth>
          <InputLabel id='spreadsheet_choose_project'>
            <Typography variant='h4'>
              <FormattedMessage id='spreadsheet_table_project' />
            </Typography>
          </InputLabel>
          <Select
            labelId='spreadsheet_choose_project'
            variant='standard'
            onChange={handleChangeProject}
            IconComponent={() => (
              <ChevronIcon
                direction='down'
                fontSize='small'
                viewBox='0 0 24 24'
              />
            )}
            disableUnderline
            defaultValue={0}
          >
            <MenuItem disabled value={0}>
              <Typography variant='h4'>
                <FormattedMessage id='spreadsheet_choose_project' />
              </Typography>
            </MenuItem>
            {anotherProjects.map(project => (
              <MenuItem key={project.projectId} value={project.projectId}>
                <Typography variant='h4'>{project.projectName}</Typography>
              </MenuItem>
            ))}
          </Select>
        </StyledProjectSelectMobile>
        <StyledProjectSelectMobile fullWidth>
          <InputLabel id='spreadsheet_choose_task'>
            <Typography variant='h4'>
              <FormattedMessage id='spreadsheet_table_issues' />
            </Typography>
          </InputLabel>
          <Select
            labelId='spreadsheet_choose_task'
            variant='standard'
            IconComponent={() => (
              <ChevronIcon
                direction='down'
                fontSize='small'
                viewBox='0 0 24 24'
              />
            )}
            onChange={handleChangeProjectTask}
            disabled={!newIssue.projectId}
            disableUnderline
            defaultValue={0}
            value={newIssue.issueUniqueId}
          >
            <MenuItem disabled value={0}>
              <Typography variant='h4'>
                <FormattedMessage id='spreadsheet_choose_task' />
              </Typography>
            </MenuItem>
            {Object.keys(formattedAnotherProjects).map(issueKey => {
              const issue = formattedAnotherProjects[issueKey];
              return (
                newIssue.projectId === issue.projectId && (
                  <MenuItem
                    key={issue.issueUniqueId}
                    value={issue.issueUniqueId}
                  >
                    <Typography variant='h4'>{issue.issueName}</Typography>
                  </MenuItem>
                )
              );
            })}
          </Select>
        </StyledProjectSelectMobile>
        {currentWeek.map(day => {
          return (
            <SpreadsheetMobileCreateRow
              key={day.date}
              day={day}
              intl={intl}
              currentWeek={currentWeek}
              disabled={!newIssue.issueUniqueId}
              setWeekSum={setWeekSum}
              newIssue={newIssue}
              timeType={timeType}
              timeTypes={timeTypes}
              setEditedFormattedIssues={setEditedFormattedIssues}
              currentIssue={newIssue}
            />
          );
        })}
        {weekSum !== 0 && (
          <Box
            sx={{
              marginY: 2.5,
            }}
          >
            <Typography variant='h4'>
              <FormattedMessage id='spreadsheet_week_sum' /> {weekSum}{' '}
              <FormattedMessage id='hour' />
            </Typography>
          </Box>
        )}
      </Grid>
    </StyledMobileCreateRoot>
  );
};

export default SpreadsheetMobileCreate;
